import { deleteCorporateLocation, getCorporateLocations } from '../../../api/corporateApi'
import AuthPermission from '../../../high-order-components/auth-permission'
import translate from '../../../i18n'
import { ICorporateLocation } from '../../../pages/corporates-container/share-type'
import { PermissionsEnum } from '../../../share-types'
import LinkButton from '../../link-button/link-button'
import CorporateLocationModal from '../corporate-location-modal/corporate-location-modal'
import { PlusOutlined } from '@ant-design/icons'
import { Button, Modal, notification, Space, Table, Typography } from 'antd'
import { ColumnsType } from 'antd/lib/table'
import { useCallback, useEffect, useState } from 'react'

interface CorporateLocationsProps {
  corporateId: number
}

const { Title } = Typography

const CorporateLocations = ({ corporateId }: CorporateLocationsProps) => {
  const [locations, setLocations] = useState<ICorporateLocation[]>([])
  const [loading, setLoading] = useState(false)
  const [modalOpen, setModalOpen] = useState(false)
  const [editingLocation, setEditingLocation] = useState<ICorporateLocation>()

  const loadLocations = useCallback(() => {
    setLoading(true)
    return getCorporateLocations(corporateId)
      .then(setLocations)
      .catch(error => {
        notification.error({
          message: translate('corporate.location.loadFailed'),
          description: error?.message,
        })
      })
      .finally(() => setLoading(false))
  }, [corporateId])

  useEffect(() => {
    loadLocations()
  }, [loadLocations])

  const closeModal = () => {
    setModalOpen(false)
    setEditingLocation(undefined)
  }

  const handleSaved = () => {
    closeModal()
    loadLocations()
  }

  const handleDelete = (location: ICorporateLocation) => {
    Modal.confirm({
      title: translate('corporate.location.deleteConfirm'),
      content: location.address,
      onOk: () =>
        deleteCorporateLocation(corporateId, location.id)
          .then(() => {
            notification.success({ message: translate('corporate.location.deleteSuccess') })
            loadLocations()
          })
          .catch(error => {
            notification.error({
              message: translate('corporate.location.deleteFailed'),
              description: error?.message,
            })
          }),
    })
  }

  const columns: ColumnsType<ICorporateLocation> = [
    { title: translate('corporate.location.city'), dataIndex: 'cityName', key: 'cityName' },
    {
      title: translate('corporate.location.businessDistrict'),
      dataIndex: 'businessDistrictName',
      key: 'businessDistrictName',
      render: value => value || '-',
    },
    { title: translate('corporate.location.address'), dataIndex: 'address', key: 'address' },
    {
      title: translate('corporate.location.longitude'),
      dataIndex: 'longitude',
      key: 'longitude',
      width: 120,
    },
    {
      title: translate('corporate.location.latitude'),
      dataIndex: 'latitude',
      key: 'latitude',
      width: 120,
    },
    {
      title: translate('corporate.location.memberCount'),
      dataIndex: 'memberCount',
      key: 'memberCount',
      width: 140,
    },
    {
      title: translate('tableActionColumn'),
      key: 'action',
      width: 220,
      render: (_, record) => (
        <Space>
          <LinkButton to={`/corporates/${corporateId}/nearby-studios?locationId=${record.id}`}>
            {translate('corporate.location.nearbyStudios')}
          </LinkButton>
          <AuthPermission permission={PermissionsEnum.CORPORATES_WRITE}>
            <Button
              type="link"
              onClick={() => {
                setEditingLocation(record)
                setModalOpen(true)
              }}
            >
              {translate('edit')}
            </Button>
            <Button type="link" danger onClick={() => handleDelete(record)}>
              {translate('delete')}
            </Button>
          </AuthPermission>
        </Space>
      ),
    },
  ]

  return (
    <Space className="table-space" orientation="vertical" size={16}>
      <Space className="table-space" align="center" style={{ justifyContent: 'space-between' }}>
        <Title level={5}>{translate('corporate.location.title')}</Title>
        <AuthPermission permission={PermissionsEnum.CORPORATES_WRITE}>
          <Button
            type="primary"
            icon={<PlusOutlined />}
            onClick={() => {
              setEditingLocation(undefined)
              setModalOpen(true)
            }}
          >
            {translate('corporate.location.add')}
          </Button>
        </AuthPermission>
      </Space>
      <Table
        rowKey="id"
        loading={loading}
        columns={columns}
        dataSource={locations}
        pagination={false}
      />
      <CorporateLocationModal
        open={modalOpen}
        corporateId={corporateId}
        location={editingLocation}
        onCancel={closeModal}
        onSaved={handleSaved}
      />
    </Space>
  )
}

export default CorporateLocations
